import { useServices } from '@/models/services';
import { Box, Button, Flex, List, Modal, Text, Title } from '@mantine/core';
import { PiLink, PiTrash, PiWarning } from 'react-icons/pi';

interface ServiceDeletionConfirmationProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  serviceName: string;
  parentIds: number[];
}

export default function ServiceDeletionConfirmation({
  opened,
  onClose,
  onConfirm,
  serviceName,
  parentIds,
}: ServiceDeletionConfirmationProps) {
  const { data: parentServices = [], isLoading } = useServices(parentIds);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Flex align="center" gap="sm">
          <PiWarning size={20} color="orange" />
          <Title order={4}>Delete Service</Title>
        </Flex>
      }
      centered
      size="md"
    >
      <Box>
        <Text mb="md">
          Are you sure you want to delete{' '}
          <Text component="span" fw={500}>
            "{serviceName}"
          </Text>
          ?
        </Text>

        {parentIds.length > 0 && (
          <Box mb="md">
            <Text size="sm" fw={500} mb="xs" c="orange">
              This service will be removed from the following parent services:
            </Text>
            {isLoading ? (
              <Text size="sm" c="dimmed">
                Loading parent services...
              </Text>
            ) : parentServices.length > 0 ? (
              <List
                size="sm"
                spacing="xs"
                icon={<PiLink size={14} />}
                styles={{
                  root: {
                    backgroundColor: 'var(--mantine-color-orange-0)',
                    padding: 'var(--mantine-spacing-sm)',
                    borderRadius: 'var(--mantine-radius-sm)',
                  },
                }}
              >
                {parentServices
                  .filter((service) => service) // Filter out null/undefined services
                  .map((service) => (
                    <List.Item key={service!.id}>
                      <Text size="sm">{service!.name}</Text>
                    </List.Item>
                  ))}
              </List>
            ) : (
              <Text size="sm" c="dimmed">
                No parent services found.
              </Text>
            )}
          </Box>
        )}

        <Text size="sm" c="red" mb="lg">
          This action cannot be undone.
        </Text>

        <Flex justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<PiTrash size={16} />}
            onClick={handleConfirm}
          >
            Delete Service
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
}
